/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { WorktypesService } from '../worktypes.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { Worktype, WorktypeDTO } from 'src/app/_model/workttype';
import { WorktypesDaoService } from '../../dao/worktypes-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads App Parameters
 */
@Injectable()
export class WorktypesLogicService implements WorktypesService {
  public worktypes$ = new BehaviorSubject<Worktype[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private worktypeDaoService: WorktypesDaoService,
    private authenticationService: AuthenticationService
  ) {
    this.initialize();
  }

  // Public Functions
  public getWorktypeById(id: IMSId): Worktype {
    if (id === undefined) {
      return undefined
    }

    return this.worktypes$.getValue().find((sj) => {
      if (sj.id.imsId == id.imsId && sj.id.secondaryId == id.secondaryId) {
        return true;
      }
      return false;
    });
  }

  public getDefaultWorktype(): Worktype {
    return this.worktypes$.getValue().find((wt) => {
      if (wt.syscode === "COMMON") {
        return true;
      }
      return false
    })
  }
  public getDefaultCustomerWorktype(): Worktype {
    return this.worktypes$.getValue().find((wt) => {
      if (wt.syscode === "CLIENT") {
        return true;
      }
      return false
    })
  }

  // Resetable
  public async reset() {
    await this.worktypeDaoService.reset();
  }

  public async clear() {
    await this.worktypeDaoService.clear();
    this.worktypes$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteWorktypes(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}


  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalWorktypes();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }
  
  // Private Functions
  private async loadLocalWorktypes() {
    try {
      this.logger.log(this.constructor.name, '- Load local Worktypes');
      let worktypes: Worktype[] = await this.worktypeDaoService.getWorktypes();
      console.log("JBWT worktypes", worktypes)

      this.worktypes$.next(worktypes);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Worktypes: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteWorktypes(apiLevel: APILevel, syncKey: string ) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load remote Worktypes from IMS'
      );

      try {
        let worktypes: Worktype[] = [];
        let worktypesDto: WorktypeDTO[] = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'syncworktypes',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );

        for (const worktypeDto of worktypesDto) {
          worktypes.push(Worktype.fromDto(worktypeDto));
        }

        this.logger.log(
          this.constructor.name,
          'Downloaded Worktypes: ',
          worktypes
        );

        await this.worktypeDaoService.saveWorktypes(worktypes);
        await this.worktypes$.next(worktypes);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Worktypes: ',
          error?.message
        );
        throw error;
      }
    } else {
      let error = new Error(
        'Cannot download Worktypes - user not authenticated'
      );
      this.logger.error(this.constructor.name, error?.message);
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
